class SearchOverlay extends HTMLElement {
  connectedCallback() {
    this.backdrop = this.querySelector('[data-search-backdrop]');
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.predictiveSearchUrl = this.dataset.predictiveSearchUrl;
    this.openTriggers = document.querySelectorAll('[data-search-open]');
    this.closeTriggers = this.querySelectorAll('[data-search-close]');
    this.abortController = null;

    this.openTriggers.forEach((trigger) => trigger.addEventListener('click', () => this.open()));
    this.closeTriggers.forEach((trigger) => trigger.addEventListener('click', () => this.close()));
    this.backdrop.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });

    // Same back/forward-cache guard as the mobile menu: a restored page must never come
    // back with the scroll lock still applied, or it looks frozen until a reload.
    window.addEventListener('pageshow', () => this.close());

    let debounceId;
    this.input.addEventListener('input', (event) => {
      clearTimeout(debounceId);
      const term = event.target.value.trim();
      debounceId = setTimeout(() => this.search(term), 250);
    });
  }

  /*
   * No scroll lock here, unlike the cart and mobile-menu drawers.
   *
   * The lock sets `overflow: hidden` on <html> and <body>, and `position: sticky` stops
   * working as soon as an ancestor scrolls-hides its overflow — so the sticky header
   * dropped back to its static position the moment the search opened. Scrolled down the
   * page, that put it off-screen and the hero showed through where the header had been.
   *
   * This panel is a dropdown under the header, not a full-screen modal, so letting the
   * page scroll behind it is both acceptable and how site search normally behaves. The
   * panel scrolls its own results; `overscroll-behavior` in the stylesheet stops that
   * from chaining to the page.
   */
  open() {
    this.classList.add('is-open');
    this.backdrop.dataset.open = 'true';
    this.input.focus({ preventScroll: true });
  }

  close() {
    this.classList.remove('is-open');
    this.backdrop.dataset.open = 'false';
  }

  async search(term) {
    if (term.length < 2) {
      this.results.innerHTML = '';
      return;
    }
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      // `resources[type]` and `[limit]` are explicit: without them Shopify decides
      // which resource types to return, and the section only renders products.
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product',
        'resources[limit]': '6',
        section_id: 'predictive-search',
      });
      const response = await fetch(`${this.predictiveSearchUrl}?${params}`, {
        signal: this.abortController.signal,
      });
      if (!response.ok) return;
      this.results.innerHTML = await response.text();
    } catch (error) {
      if (error.name !== 'AbortError') this.results.innerHTML = '';
    }
  }
}

if (!customElements.get('search-overlay-component')) {
  customElements.define('search-overlay-component', SearchOverlay);
}
